import { useMutation, useQuery, useQueryClient, queryOptions } from '@tanstack/react-query';
import {
  CreateCredentialRequest,
  CreateCredentialResponse,
  KeepaBalanceResponse,
  ListCredentialsResponse,
  TavilyBalanceResponse,
  ValidateCredentialResponse,
} from '@sourcito/schemas';
import { apiFetch } from './api';

export const credentialsQuery = queryOptions({
  queryKey: ['credentials'] as const,
  queryFn: () =>
    apiFetch<ListCredentialsResponse>('/api/credentials', {
      responseSchema: ListCredentialsResponse,
    }),
  staleTime: 30_000,
});

export function useCredentials() {
  return useQuery(credentialsQuery);
}

export function useCreateCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCredentialRequest) =>
      apiFetch<CreateCredentialResponse>('/api/credentials', {
        method: 'POST',
        body: JSON.stringify(input),
        responseSchema: CreateCredentialResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credentials'] });
    },
  });
}

export function useDeleteCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<null>(`/api/credentials/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credentials'] });
    },
  });
}

export const keepaBalanceQuery = queryOptions({
  queryKey: ['credentials', 'keepa', 'balance'] as const,
  queryFn: () =>
    apiFetch<KeepaBalanceResponse>('/api/credentials/keepa/balance', {
      responseSchema: KeepaBalanceResponse,
    }),
  // Balance changes as searches consume tokens; refetch on every mount but
  // don't auto-poll (the explicit refresh button + post-search invalidation
  // covers staleness).
  staleTime: 0,
  retry: false,
});

export function useKeepaBalance() {
  return useQuery({ ...keepaBalanceQuery, enabled: true });
}

export const tavilyBalanceQuery = queryOptions({
  queryKey: ['credentials', 'tavily', 'balance'] as const,
  queryFn: () =>
    apiFetch<TavilyBalanceResponse>('/api/credentials/tavily/balance', {
      responseSchema: TavilyBalanceResponse,
    }),
  // Same staleness contract as Keepa balance: refetch on mount, don't poll.
  // Tavily /usage is free so a more-aggressive refetch is also acceptable
  // if the dashboard UX wants it later.
  staleTime: 0,
  retry: false,
});

export function useTavilyBalance() {
  return useQuery({ ...tavilyBalanceQuery, enabled: true });
}

export function useValidateCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<ValidateCredentialResponse>(`/api/credentials/${id}/validate`, {
        method: 'POST',
        responseSchema: ValidateCredentialResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credentials'] });
    },
  });
}
